import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { refreshTopicHierarchyAndCache } from '@/utils/cache';
import { 
  activeImportJobsAtom, 
  updateImportJobAtom, 
  removeImportJobAtom 
} from '@/stores/importJobStore';

/**
 * Global hook that polls for import job status
 * This should be used at the app root level to ensure continuous polling
 */
export function useImportJobPoller() {
  const [activeJobs] = useAtom(activeImportJobsAtom);
  const updateJob = useSetAtom(updateImportJobAtom);
  const removeJob = useSetAtom(removeImportJobAtom);

  // Get the first active processing job
  const activeJobId = Array.from(activeJobs.values())
    .find(job => job.status === 'processing' || job.status === 'pending')?.id;

  // Poll for job status
  const { data: jobStatus } = useQuery(
    trpc.integrations.getImportJobStatus.queryOptions(
      { jobId: activeJobId || '' },
      { 
        enabled: !!activeJobId,
        refetchInterval: (data) => {
          // Stop polling if job is completed or failed
          if (data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled') {
            return false;
          }
          // Poll every 2 seconds while processing
          return 2000;
        },
      }
    )
  );

  // Handle job status updates
  useEffect(() => {
    if (!jobStatus || !activeJobId) return;

    const job = activeJobs.get(activeJobId);
    if (!job) return;

    // Update progress
    updateJob({
      jobId: activeJobId,
      updates: {
        status: jobStatus.status,
        progress: jobStatus.progress || 0,
      }
    });

    // Handle completion
    if (jobStatus.status === 'completed') {
      const handleCompletion = async () => {
        // Refresh topic hierarchy and clear document cache
        await refreshTopicHierarchyAndCache(job.workspaceId);
        
        // Show success notification
        toast.success(
          'Import completed successfully!',
          {
            description: 'Your content has been refreshed.',
            duration: 5000,
          }
        );

        // Mark as completed
        updateJob({
          jobId: activeJobId,
          updates: {
            status: 'completed',
            completedAt: new Date(),
          }
        });

        // Remove from active jobs after a delay
        setTimeout(() => {
          removeJob(activeJobId);
        }, 5000);
      };

      handleCompletion();
    } else if (jobStatus.status === 'failed' || jobStatus.status === 'cancelled') {
      // Show error notification
      toast.error(
        `Import ${jobStatus.status}`,
        {
          description: jobStatus.errors?.[0] || 'Please try again.',
          duration: 5000,
        }
      );

      // Remove from active jobs after a delay
      setTimeout(() => {
        removeJob(activeJobId);
      }, 5000);
    }
  }, [jobStatus, activeJobId, activeJobs, updateJob, removeJob]);

  return {
    activeJobs: Array.from(activeJobs.values()),
    hasActiveJobs: activeJobs.size > 0,
  };
}